import { useDocumentTitle } from "@/lib/use-document-title";
import { useState, useMemo } from "react";
import { CustomerLayout } from "@/components/CustomerLayout";
import { BenefitActivationModal } from "@/components/BenefitActivationModal";
import { BookingModal } from "@/components/BookingModal";
import {
  useBenefitDefinitions,
  useCurrentMember,
  useBenefitUsageThisMonth,
  formatNextResetLabel,
  type BenefitDefinition,
} from "@/lib/data-hooks";
import {
  Tag, Search, Zap, Star, Gift, Sparkles, Shield, Calendar,
  Umbrella, Ticket, CheckCircle2, Info, X, AlertCircle, Store,
} from "lucide-react";
import { EmptyState, CardSkeletonGrid } from "@/components/EmptyState";

const iconMap = {
  tag: Tag, search: Search, zap: Zap, star: Star, handshake: Gift, sparkles: Sparkles,
  shield: Shield, calendar: Calendar, umbrella: Umbrella, ticket: Ticket,
} as const;

function BenefitsPage() {
  useDocumentTitle("Förmåner — Lagamobilen Access");
  const member = useCurrentMember();
  const { data: benefits = [], isLoading } = useBenefitDefinitions({ onlyActive: true });
  const { data: usageLogs = [] } = useBenefitUsageThisMonth(member?.id ?? null);
  const [activateId, setActivateId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const usedIds = useMemo(
    () => new Set(usageLogs.map((u: any) => u.benefit_definition_id ?? u.benefit_type).filter(Boolean)),
    [usageLogs],
  );
  const resetLabel = formatNextResetLabel();

  const unlimited = benefits.filter((b) => b.category === "unlimited");
  const limited = benefits.filter((b) => b.category === "limited");
  const extra = benefits.filter((b) => b.category === "extra");

  const unused = limited.filter((b) => !usedIds.has(b.id)).length;
  const activateTarget = benefits.find((b) => b.id === activateId);
  const bookingTarget = limited.find((b) => b.id === bookingId);

  const renderIcon = (icon: string, size = 28) => {
    const Icon = iconMap[icon as keyof typeof iconMap] ?? Star;
    return <Icon className="text-primary mb-3" size={size} />;
  };

  return (
    <CustomerLayout title="Dina förmåner">
      <p className="text-muted-foreground mb-8 max-w-2xl">Utnyttja dina obegränsade förmåner närsomhelst. Begränsade förmåner (1× per månad) aktiveras antingen genom bokning eller på plats i butik – beroende på förmånens typ.</p>

      {isLoading && <CardSkeletonGrid count={6} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" />}

      {!isLoading && !benefits.length && (
        <EmptyState
          icon={Sparkles}
          title="Inga förmåner upplagda ännu"
          description="Så snart din butikskedja lägger upp förmåner dyker de upp här."
          ctaLabel="Tillbaka till översikten"
          ctaTo="/access/dashboard"
        />
      )}

      {member && unused > 0 && (
        <div className="flex items-start gap-3 p-4 mb-8 rounded-xl border border-[var(--color-gold)]/40 bg-[var(--color-gold)]/5 max-w-3xl">
          <AlertCircle className="text-[var(--color-gold)] shrink-0 mt-0.5" size={20} />
          <div className="text-sm">Du har <strong>{unused}</strong> oanvänd{unused === 1 ? "" : "a"} förmån{unused === 1 ? "" : "er"} denna månad – de förfaller den sista i månaden.</div>
        </div>
      )}

      {!!unlimited.length && <>
        <h2 className="mb-4">Obegränsade förmåner</h2>
        <p className="text-xs text-muted-foreground mb-4">Aktiveras varje gång du nyttjar dem i butik – så vi kan visa hur ofta du använder ditt medlemskap.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {unlimited.map((b) => (
            <div key={b.id} className="card-soft p-6">
              {renderIcon(b.icon)}
              <h3 className="font-display font-semibold mb-1">{b.name}</h3>
              <p className="text-sm text-muted-foreground">{b.description}</p>
              {b.display_only ? (
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-primary font-display font-semibold">
                  <Sparkles size={14} /> Ingår automatiskt i ditt medlemskap
                </div>
              ) : (
                <>
                  <button onClick={() => setActivateId(b.id)} className="btn-pill mt-4 !py-2 !px-5 !text-sm">
                    <Store size={14} className="inline mr-1.5 -mt-0.5" />Aktivera i butik
                  </button>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--color-success)] font-display font-semibold">
                    <CheckCircle2 size={14} /> Alltid tillgänglig
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </>}

      {!!limited.length && <>
        <h2 className="mb-4">Begränsade förmåner <span className="text-sm font-normal text-muted-foreground">(1× per månad)</span></h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {limited.map((b) => {
            const used = usedIds.has(b.id);
            return (
              <div key={b.id} className="card-soft p-6 relative">
                <span className="absolute top-4 right-4 text-[10px] font-display font-bold tracking-wider uppercase bg-[var(--color-gold)]/15 text-[var(--color-gold)] px-2 py-1 rounded">1× / månad</span>
                {renderIcon(b.icon)}
                <h3 className="font-display font-semibold mb-1 pr-16">{b.name}</h3>
                <p className="text-sm text-muted-foreground">{b.description}</p>
                {used ? (
                  <div className="mt-4">
                    <div className="inline-flex items-center gap-1.5 text-sm text-[var(--color-success)] font-display font-semibold">
                      <CheckCircle2 size={16} /> Använd denna månad
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{resetLabel}</div>
                  </div>
                ) : b.activation_type === "booking" ? (
                  <>
                    <button onClick={() => setBookingId(b.id)} className="btn-pill mt-4 !py-2 !px-5 !text-sm"><Calendar size={14} className="inline mr-1.5 -mt-0.5" />Boka tid</button>
                    <div className="text-[11px] text-muted-foreground mt-2">{resetLabel}</div>
                  </>
                ) : (
                  <>
                    <button onClick={() => setActivateId(b.id)} className="btn-pill mt-4 !py-2 !px-5 !text-sm"><Store size={14} className="inline mr-1.5 -mt-0.5" />Aktivera i butik</button>
                    <div className="text-[11px] text-muted-foreground mt-2">Aktiveras endast på plats med personal · {resetLabel}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </>}

      {!!extra.length && <>
        <h2 className="mb-4">Övriga förmåner</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {extra.map((b: BenefitDefinition) => (
            <div key={b.id} className="card-soft p-6 flex items-start gap-4">
              {renderIcon(b.icon, 28)}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-semibold">{b.name}</h3>
                  {b.name.toLowerCase().includes("solid") && (
                    <button onClick={() => setInfoOpen(true)} aria-label="Info" className="text-muted-foreground hover:text-primary"><Info size={16} /></button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{b.description}</p>
              </div>
            </div>
          ))}
        </div>
      </>}

      <BenefitActivationModal
        open={!!activateTarget}
        onClose={() => setActivateId(null)}
        benefitDefinitionId={activateTarget?.id ?? null}
        benefitTitle={activateTarget?.name ?? ""}
      />

      <BookingModal
        open={!!bookingTarget}
        onClose={() => setBookingId(null)}
        benefitDefinitionId={bookingTarget?.id ?? null}
        benefitTitle={bookingTarget?.name ?? ""}
        onConfirmed={() => { /* refresh handled via query invalidation */ }}
      />

      {infoOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setInfoOpen(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 relative" onClick={e => e.stopPropagation()}>
            <button className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" onClick={() => setInfoOpen(false)}><X size={18} /></button>
            <h3 className="!text-xl font-display font-bold mb-2">Solid Försäkring – 49kr/mån</h3>
            <p className="text-sm text-muted-foreground">Som Access-medlem får du Solid Mobilförsäkring för endast 49kr/mån (ord. 89kr). Försäkringen täcker skärmsprickor, vätska, stöld och tekniska fel. Tecknas i butik – fråga personalen vid nästa besök.</p>
            <button onClick={() => setInfoOpen(false)} className="btn-pill mt-5">Stäng</button>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
}

export default BenefitsPage;
